import {FormControl, FormField} from 'platform/components';
import {Grid, Heading, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {always} from 'ramda';

import {
  basePriceSourceObject,
  GetArticleResponse,
  salesPriceCalculationObject,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';

import {ArticleDetailForm} from '../types/ArticleDetailFormSchema';

interface PricesTabSettingsFormProps {
  control: FormControl<ArticleDetailForm>;
  article: GetArticleResponse;
}

export function PricesTabSettingsForm(props: PricesTabSettingsFormProps) {
  const recommendedPriceSource = props.article.pricesSettings?.recommendedPriceSource;
  const purchasePriceSource = props.article.pricesSettings?.purchasePriceDefSupplierSource;

  const recommendedPriceSourceHelperText = match(recommendedPriceSource)
    .with(Pattern.nullish, always(undefined))
    .with(
      'DELIVERY_NOTE',
      always(
        i18n.t('entity.warehouse.labels.recommendedPriceSource', {
          source: i18n.t('entity.warehouse.labels.deliveryNote'),
        })
      )
    )
    .with(
      'PRICE_LIST',
      always(
        i18n.t('entity.warehouse.labels.recommendedPriceSource', {
          source: i18n.t('entity.warehouse.labels.supplierPriceList'),
        })
      )
    )
    .with(
      'MANUAL',
      always(
        i18n.t('entity.warehouse.labels.recommendedPriceSource', {
          source: i18n.t('entity.warehouse.labels.manuallySet'),
        })
      )
    )
    .otherwise(always(undefined));

  const purchasePriceSourceHelperText = match(purchasePriceSource)
    .with(Pattern.nullish, always(undefined))
    .with(
      'DELIVERY_NOTE',
      always(
        i18n.t('entity.warehouse.labels.purchasePriceSource', {
          source: i18n.t('entity.warehouse.labels.deliveryNote'),
        })
      )
    )
    .with(
      'PRICE_LIST',
      always(
        i18n.t('entity.warehouse.labels.purchasePriceSource', {
          source: i18n.t('entity.warehouse.labels.supplierPriceList'),
        })
      )
    )
    .with(
      'MANUAL',
      always(
        i18n.t('entity.warehouse.labels.purchasePriceSource', {
          source: i18n.t('entity.warehouse.labels.manuallySet'),
        })
      )
    )
    .otherwise(always(undefined));

  return (
    <>
      <VStack height="100%" spacing={3}>
        <Heading size={4}>{i18n.t('general.labels.settings')}</Heading>
        <Grid columns={4} spacing={8}>
          <VStack spacing={3}>
            <FormField
              control={props.control}
              isDisabled
              type="currency"
              isAutoselectedOnFocus
              name="prices.pricesSettings.avgPurchasePriceWithoutVat"
              label={i18n.t('warehouse.labels.avgPurchasePriceWithoutVat')}
              data-testid={testIds.warehouse.articleDetailOverview('avgPurchasePriceWithoutVat')}
            />
            <FormField
              control={props.control}
              isDisabled
              type="currency"
              isAutoselectedOnFocus
              name="prices.pricesSettings.lastPurchasePriceWithoutVat"
              label={i18n.t('warehouse.labels.lastPurchasePriceWithoutVat')}
              data-testid={testIds.warehouse.articleDetailOverview('lastPurchasePriceWithoutVat')}
            />
            <FormField
              control={props.control}
              isDisabled
              type="currency"
              isAutoselectedOnFocus
              name="prices.pricesSettings.recommendedPurchasePriceWithoutVat"
              label={i18n.t('warehouse.labels.recommendedPurchasePriceWithoutVat')}
              helperText={recommendedPriceSourceHelperText}
              data-testid={testIds.warehouse.articleDetailOverview(
                'recommendedPurchasePriceWithoutVat'
              )}
            />
            <FormField
              control={props.control}
              isDisabled
              type="currency"
              isAutoselectedOnFocus
              name="prices.pricesSettings.purchasePriceDefSupplierWithoutVat"
              label={i18n.t('warehouse.labels.purchasePriceDefSupplierWithoutVat')}
              helperText={purchasePriceSourceHelperText}
              data-testid={testIds.warehouse.articleDetailOverview(
                'purchasePriceDefSupplierWithoutVat'
              )}
            />
          </VStack>
          <VStack spacing={3}>
            <FormField
              control={props.control}
              isDisabled
              type="currency"
              isAutoselectedOnFocus
              name="prices.pricesSettings.avgPurchasePriceWithVat"
              label={i18n.t('warehouse.labels.avgPurchasePriceWithVat')}
              data-testid={testIds.warehouse.articleDetailOverview('avgPurchasePriceWithVat')}
            />
            <FormField
              control={props.control}
              isDisabled
              type="currency"
              isAutoselectedOnFocus
              name="prices.pricesSettings.lastPurchasePriceWithVat"
              label={i18n.t('warehouse.labels.lastPurchasePriceWithVat')}
              data-testid={testIds.warehouse.articleDetailOverview('lastPurchasePriceWithVat')}
            />
            <FormField
              control={props.control}
              isDisabled
              type="currency"
              isAutoselectedOnFocus
              name="prices.pricesSettings.recommendedPurchasePriceWithVat"
              label={i18n.t('warehouse.labels.recommendedPurchasePriceWithVat')}
              data-testid={testIds.warehouse.articleDetailOverview(
                'recommendedPurchasePriceWithVat'
              )}
            />
            <FormField
              control={props.control}
              isDisabled
              type="currency"
              isAutoselectedOnFocus
              name="prices.pricesSettings.purchasePriceDefSupplierWithVat"
              label={i18n.t('warehouse.labels.purchasePriceDefSupplierWithVat')}
              data-testid={testIds.warehouse.articleDetailOverview(
                'purchasePriceDefSupplierWithVat'
              )}
            />
          </VStack>
          <VStack spacing={3}>
            <FormField
              control={props.control}
              type="radio"
              direction="column"
              spacing={2}
              name="prices.pricesSettings.salesPriceCalculation"
              label={i18n.t('warehouse.labels.salesPriceCalculation')}
              options={salesPriceCalculationOptions}
              data-testid={testIds.warehouse.articleDetailOverview('salesPriceCalculation')}
            />
            <FormField
              isDisabled
              control={props.control}
              type="switch"
              name="prices.pricesSettings.unitSalesPriceWithVat"
              label={i18n.t('warehouse.labels.unitSalesPriceWithVat')}
              data-testid={testIds.warehouse.articleDetailOverview('unitSalesPriceWithVat')}
            />
          </VStack>
          <VStack spacing={3}>
            <FormField
              control={props.control}
              type="radio"
              direction="column"
              spacing={2}
              name="prices.pricesSettings.basePriceSource"
              label={i18n.t('warehouse.labels.basePriceSource')}
              options={basePriceCalculationOptions}
              data-testid={testIds.warehouse.articleDetailOverview('basePriceSource')}
            />
          </VStack>
        </Grid>
      </VStack>
    </>
  );
}

const salesPriceCalculationOptions = [
  {
    label: i18n.t('general.labels.salesPriceCalculationMargin'),
    value: salesPriceCalculationObject.MARGIN_ON_BASE,
  },
  {
    label: i18n.t('general.labels.salesPriceCalculationMarkup'),
    value: salesPriceCalculationObject.MARKUP_ON_BASE,
  },
  {
    label: i18n.t('general.labels.salesPriceCalculationProfitOnBase'),
    value: salesPriceCalculationObject.PROFIT_ON_BASE,
  },
  {
    label: i18n.t('general.labels.salesPriceCalculationManual'),
    value: salesPriceCalculationObject.MANUAL,
  },
  {
    label: i18n.t('general.labels.salesPriceCalculationRecommendedPrice'),
    value: salesPriceCalculationObject.RECOMMENDED_PRICE,
  },
  {
    label: i18n.t('general.labels.salesPriceCalculationMarketingCode'),
    value: salesPriceCalculationObject.MARKETING_CODE,
  },
];

const basePriceCalculationOptions = [
  {
    label: i18n.t('general.labels.purchasePriceAtDefaultSupplier'),
    value: basePriceSourceObject.PURCHASE_PRICE_DEF_SUPPLIER,
  },
  {
    label: i18n.t('general.labels.basePriceCalculationAveragePurchasePrice'),
    value: basePriceSourceObject.AVG_PURCHASE,
  },
  {
    label: i18n.t('general.labels.basePriceCalculationLastPurchasePrice'),
    value: basePriceSourceObject.LAST_PURCHASE,
  },
  {
    label: i18n.t('general.labels.basePriceCalculationLastRecommendedPrice'),
    value: basePriceSourceObject.RECOMMENDED,
  },
  {
    label: i18n.t('general.labels.basePriceCalculationManual'),
    value: basePriceSourceObject.MANUAL,
  },
];
